import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { imgUrl } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Car, LogIn, UploadCloud, X, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { INDIA_STATES } from "@/data/indiaLocations";
import { VerifyDialog } from "@/components/VerifyDialog";

const BRANDS = ["Maruti Suzuki", "Hyundai", "Tata", "Mahindra", "Honda", "Toyota", "Kia", "Volkswagen", "BMW", "Mercedes-Benz", "Other"];
const BODY = ["Hatchback", "Sedan", "SUV", "MUV", "Luxury"];
const FUELS = ["Petrol", "Diesel", "CNG", "Electric", "Hybrid"];
const TRANS = ["Manual", "Automatic"];

export default function PostVehicle() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", brand: "Maruti Suzuki", model: "", year: "2020", price: "",
    km_driven: "", fuel_type: "Petrol", transmission: "Manual", owners: "1", body_type: "Hatchback",
    color: "", state: "Karnataka", city: "", contact_name: user?.name || "", contact_phone: "",
  });

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <div className="h-14 w-14 rounded-2xl bg-navy/10 flex items-center justify-center mx-auto"><LogIn className="h-7 w-7 text-navy" /></div>
        <h1 className="mt-5 font-display text-2xl font-semibold text-slate-900">Sign in to sell your car</h1>
        <Button data-testid="vehicle-login-btn" onClick={login} className="mt-6 bg-navy hover:bg-navy-dark">Sign in with Google</Button>
      </div>
    );
  }

  if (!user.verified) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <div className="h-14 w-14 rounded-2xl bg-gold/20 flex items-center justify-center mx-auto"><ShieldAlert className="h-7 w-7 text-gold-dark" /></div>
        <h1 className="mt-5 font-display text-2xl font-semibold text-slate-900">Verify your identity to sell</h1>
        <p className="mt-2 text-slate-500">Only KYC-verified sellers can list vehicles on VYAPAAR Motors. It keeps buyers safe.</p>
        <div className="mt-6"><VerifyDialog trigger={<Button data-testid="vehicle-verify-btn" className="bg-navy hover:bg-navy-dark text-white">Verify Now</Button>} /></div>
      </div>
    );
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, res.data.url]);
        toast.success(`${file.name} uploaded`);
      } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (images.length < 5) { toast.error("Please upload at least 5 high-definition photos."); return; }
    if (!form.city) { toast.error("Please select a district / city."); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: parseInt(form.year, 10),
        price: parseInt(form.price, 10),
        km_driven: parseInt(form.km_driven, 10),
        owners: parseInt(form.owners, 10),
        images,
      };
      const res = await api.post("/vehicles", payload);
      toast.success("Car listed successfully!");
      navigate(`/motors/${res.data.id}`);
    } catch { toast.error("Could not list vehicle. Check all fields."); }
    finally { setSaving(false); }
  };

  const field = (label, key, props = {}) => (
    <div><Label className="text-xs">{label}</Label><Input data-testid={`vehicle-${key}`} value={form[key]} onChange={(e) => set(key, e.target.value)} {...props} /></div>
  );
  const sel = (label, key, opts, testid) => (
    <div><Label className="text-xs">{label}</Label>
      <Select value={form[key]} onValueChange={(v) => set(key, v)}>
        <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-72">{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 py-10">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-gold flex items-center justify-center"><Car className="h-6 w-6 text-navy" /></div>
        <div><h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">Sell your car</h1><p className="text-slate-500 text-sm">List on VYAPAAR Motors — free & verified.</p></div>
      </div>

      <form onSubmit={submit} className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
        <div>
          <Label className="text-xs">Photos</Label>
          <p className="text-xs text-slate-400 mb-2">Upload at least 5 clear, high-definition photos (min 1024×768px).</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {images.map((url) => (
              <div key={url} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 group">
                <img src={imgUrl(url)} alt="" className="w-full h-full object-cover" />
                <button type="button" data-testid="vehicle-remove-image" onClick={() => setImages((p) => p.filter((u) => u !== url))} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity"><X className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            <label data-testid="vehicle-upload" className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-navy hover:bg-navy/5 transition-colors text-slate-500">
              {uploading ? <Loader2 className="h-6 w-6 animate-spin text-navy" /> : <><UploadCloud className="h-6 w-6" /><span className="text-[11px] mt-1 font-medium">Add photos</span></>}
              <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleFiles} className="hidden" disabled={uploading} />
            </label>
          </div>
          {images.length < 5 && (
            <p className="mt-2 text-xs text-slate-400">{images.length}/5 photos — at least 5 high-definition photos are required.</p>
          )}
        </div>

        {field("Ad Title", "title", { required: true, placeholder: "e.g. 2021 Hyundai Creta SX Diesel" })}
        <div><Label className="text-xs">Description</Label><Textarea data-testid="vehicle-description" rows={3} required value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Condition, service history, features…" /></div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {sel("Brand", "brand", BRANDS, "vehicle-brand")}
          {field("Model", "model", { required: true, placeholder: "e.g. Creta" })}
          {field("Year", "year", { type: "number", required: true })}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {sel("Body Type", "body_type", BODY, "vehicle-body")}
          {sel("Fuel", "fuel_type", FUELS, "vehicle-fuel")}
          {sel("Transmission", "transmission", TRANS, "vehicle-trans")}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {field("Price (₹)", "price", { type: "number", required: true })}
          {field("KM Driven", "km_driven", { type: "number", required: true })}
          {field("Owners", "owners", { type: "number", min: 1 })}
          {field("Color", "color", { placeholder: "White" })}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label className="text-xs">State / UT</Label>
            <Select value={form.state} onValueChange={(v) => set("state", v)}>
              <SelectTrigger data-testid="vehicle-state"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">{INDIA_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {field("City", "city", { required: true, placeholder: "e.g. Pune" })}
        </div>
        <div className="grid grid-cols-2 gap-4">
          {field("Contact Name", "contact_name", { required: true })}
          {field("Contact Phone", "contact_phone", { required: true, placeholder: "+91 …" })}
        </div>

        <Button data-testid="vehicle-submit-btn" type="submit" disabled={saving} className="w-full bg-navy hover:bg-navy-dark text-white h-11">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "List Car"}
        </Button>
      </form>
    </div>
  );
}
